// SPDX-FileCopyrightText: 2024 Chris Duncan <chris@zoso.dev>
// SPDX-License-Identifier: GPL-3.0-or-later
import { blake2b } from 'blakejs';
import { ACCOUNT_KEY_LENGTH, ALPHABET, PREFIX, PREFIX_LEGACY } from './constants.js';
import { base32, bytes, hex } from './convert.js';
import Ed25519 from './ed25519.js';
import { Rpc } from './rpc.js';
import { Safe } from './safe.js';
/**
* Represents a single Nano address and the associated public key. To include the
* matching private key, it must be known at the time of object instantiation.
* The frontier, balance, and representative for the account can also be set or
* be fetched from the network.
*/
export class Account {
    #a;
    #pub;
    #prv;
    #i;
    #f;
    #b;
    #r;
    #rep;
    #w;
    #s;
    get address() { return `${PREFIX}${this.#a}`; }
    get publicKey() { return this.#pub; }
    get privateKey() { return this.#prv; }
    get index() { return this.#i; }
    get frontier() { return this.#f; }
    get balance() { return this.#b; }
    get receivable() { return this.#r; }
    get representative() { return this.#rep; }
    get weight() { return this.#w; }
    set frontier(v) { this.#f = v; }
    set balance(v) { this.#b = v ? BigInt(v) : undefined; }
    set receivable(v) { this.#r = v ? BigInt(v) : undefined; }
    set representative(v) {
        if (v?.constructor === Account) {
            this.#rep = v;
        }
        else if (typeof v === 'string') {
            this.#rep = new Account(v);
        }
        else {
            throw new TypeError(`Invalid argument for account representative: ${v}`);
        }
    }
    set weight(v) { this.#w = v ? BigInt(v) : undefined; }
    constructor(address, index) {
        Account.validate(address);
        if (index !== undefined && typeof index !== 'number') {
            throw new TypeError(`Invalid index ${index} when creating Account ${address}`);
        }
        this.#a = address
            .replace(PREFIX, '')
            .replace(PREFIX_LEGACY, '');
        this.#pub = Account.#addressToKey(this.#a);
        this.#prv = null;
        this.#i = index;
        this.#s = new Safe();
    }
    /**
    * Asynchronously instantiates an Account object from its public key.
    *
    * @param {string} key - Public key of the account
    * @param {number} [index] - Account number used when deriving the key
    * @returns {Promise<Account>} The instantiated Account object
    */
    static async fromPublicKey(key, index) {
        Account.#validateKey(key);
        const address = await Account.#keyToAddress(key);
        const account = new this(address, index);
        return account;
    }
    /**
    * Asynchronously instantiates an Account object from its private key.
    *
    * @param {string} key - Private key of the account
    * @param {number} [index] - Account number used when deriving the key
    * @returns {Promise<Account>} A new Account object
    */
    static async fromPrivateKey(key, index) {
        Account.#validateKey(key);
        const publicKey = Ed25519.getPublicKey(key);
        const account = await Account.fromPublicKey(publicKey, index);
        account.#prv = key.toUpperCase();
        return account;
    }
    async lock(passkey) {
        try {
            if (this.#prv != null) {
                await this.#s.put(this.#pub, passkey, this.#prv);
            }
        }
        catch (err) {
            console.error(`Failed to lock account ${this.address}`, err);
            return false;
        }
        this.#prv = null;
        return true;
    }
    async unlock(passkey) {
        try {
            this.#prv = await this.#s.get(this.#pub, passkey);
        }
        catch (err) {
            console.error(`Failed to unlock account ${this.address}`, err);
            return false;
        }
        return true;
    }
    /**
    * Validates a Nano address with 'nano' and 'xrb' prefixes
    * Derived from https://github.com/alecrios/nano-address-validator
    *
    * @param {string} address - Nano address to validate
    * @throws Error if address is undefined, not a string, or an invalid format
    */
    static validate(address) {
        if (address === undefined) {
            throw new ReferenceError('Address is undefined.');
        }
        if (typeof address !== 'string') {
            throw new TypeError('Address must be a string.');
        }
        const pattern = new RegExp(`^(${PREFIX}|${PREFIX_LEGACY})[13]{1}[${ALPHABET}]{59}$`);
        if (!pattern.test(address)) {
            throw new RangeError('Invalid address format');
        }
        const expectedChecksum = address.slice(-8);
        const keyBase32 = address.slice(address.indexOf('_') + 1, -8);
        const keyBuf = base32.toBytes(keyBase32);
        const actualChecksumBuf = blake2b(keyBuf, undefined, 5).reverse();
        const actualChecksum = bytes.toBase32(actualChecksumBuf);
        if (expectedChecksum !== actualChecksum) {
            throw new Error('Incorrect address checksum');
        }
    }
    /**
    * Refreshes the account from its current state on the network.
    *
    * A successful response sets the balance, frontier, and representative
    * properties.
    *
    * @param {Rpc|string|URL} rpc - RPC node information required to call `account_info`
    */
    async refresh(rpc) {
        if (typeof rpc === 'string' || rpc.constructor === URL) {
            rpc = new Rpc(rpc);
        }
        if (rpc.constructor !== Rpc) {
            throw new TypeError('RPC must be a valid node');
        }
        const data = {
            "account": this.address,
            "receivable": "true",
            "representative": "true",
            "weight": "true"
        };
        const { balance, frontier, receivable, representative, weight } = await rpc.call('account_info', data);
        if (frontier == null) {
            throw new Error('Account not found');
        }
        this.#b = BigInt(balance);
        this.#f = frontier;
        this.#r = BigInt(receivable);
        this.#rep = new Account(representative);
        this.#w = BigInt(weight);
    }
    static #addressToKey(v) {
        const keyBytes = base32.toBytes(v.substring(0, 52));
        const checksumBytes = base32.toBytes(v.substring(52, 60));
        const blakeHash = blake2b(keyBytes, undefined, 5).reverse();
        if (bytes.toHex(checksumBytes) !== bytes.toHex(blakeHash)) {
            throw new Error('Checksum mismatch in address');
        }
        return bytes.toHex(keyBytes);
    }
    static async #keyToAddress(key) {
        const publicKeyBytes = hex.toBytes(key);
        const checksum = blake2b(publicKeyBytes, undefined, 5).reverse();
        const encoded = bytes.toBase32(publicKeyBytes);
        const encodedChecksum = bytes.toBase32(checksum);
        return `${PREFIX}${encoded}${encodedChecksum}`;
    }
    static #validateKey(key) {
        if (key === undefined) {
            throw new TypeError(`Key is undefined`);
        }
        if (typeof key !== 'string') {
            throw new TypeError(`Key must be a string`);
        }
        if (key.length !== ACCOUNT_KEY_LENGTH) {
            throw new TypeError(`Key must be ${ACCOUNT_KEY_LENGTH} characters`);
        }
        if (!/^[0-9a-fA-F]+$/i.test(key)) {
            throw new RangeError('Key is not a valid hexadecimal value');
        }
    }
}
